import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  CircularProgress,
  Stack,
  Button,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const PartnerProducts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(`${API_BASE_URL}/company/products`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await response.json();
        if (data.subCode === 200 && data.items) {
          setProducts(data.items);
        } else {
          setError("Failed to load products: " + (data.message || "Unknown error"));
        }
      } catch (err) {
        setError(err.message);
        console.error("Product fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Strip HTML tags for description preview
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && products.length === 0) {
    return (
      <Box m="20px">
        <Header title="MY PRODUCTS" subtitle="View your products" />
        <Box color="error.main" p={2}>
          Error: {error}
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="MY PRODUCTS" subtitle="View and manage your products" />

      {/* Products Count */}
      <Typography variant="h6" mb={2} color={colors.grey[100]}>
        Showing {products.length} product{products.length !== 1 ? "s" : ""}
      </Typography>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
            <Card
              onClick={() => navigate(`/partner-products/${product._id}`)}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: colors.primary[400],
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              {/* Product Image */}
              {product.image && (
                <CardMedia
                  component="img"
                  height="200"
                  image={
                    product.image.startsWith("http")
                      ? product.image
                      : `https://app.airportparkwithus.co.uk/images/upload/${product.image}`
                  }
                  alt={product.company_name}
                  sx={{
                    objectFit: "contain",
                    backgroundColor: colors.primary[500],
                    p: 1,
                  }}
                  onError={(e) => {
                    e.target.src = "/assets/user.png";
                  }}
                />
              )}

              <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* Company Name */}
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={colors.grey[100]}
                  mb={1}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {product.company_name}
                </Typography>

                {/* Rating */}
                {product.company_rating_show && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <StarIcon sx={{ color: colors.greenAccent[500], fontSize: "18px", mr: 0.5 }} />
                    <Typography variant="body2" color={colors.grey[300]}>
                      {parseFloat(product.company_rating_show).toFixed(1)} ({product.company_rating})
                    </Typography>
                  </Box>
                )}

                {/* Airport & Terminals */}
                <Box mb={1}>
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <LocationOnIcon
                      sx={{ color: colors.blueAccent[500], fontSize: "16px", mr: 0.5 }}
                    />
                    <Typography variant="body2" color={colors.grey[300]}>
                      {product.airport_name || "N/A"}
                    </Typography>
                  </Box>
                  {product.terminal && product.terminal.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {product.terminal.map((term, idx) => (
                        <Chip
                          key={idx}
                          label={term.name}
                          size="small"
                          sx={{
                            backgroundColor: colors.blueAccent[700],
                            color: colors.grey[100],
                            fontSize: "0.7rem",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Description Preview */}
                <Typography
                  variant="body2"
                  color={colors.grey[400]}
                  mb={1}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    flexGrow: 1,
                  }}
                >
                  {stripHtml(product.description || product.company_info || "").substring(0, 100)}
                  ...
                </Typography>

                {/* Contact Info */}
                <Box mt="auto">
                  {product.phone && (
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <PhoneIcon
                        sx={{ color: colors.greenAccent[500], fontSize: "14px", mr: 0.5 }}
                      />
                      <Typography variant="caption" color={colors.grey[400]}>
                        {product.phone.length > 30
                          ? product.phone.substring(0, 30) + "..."
                          : product.phone}
                      </Typography>
                    </Box>
                  )}
                  {product.company_email && (
                    <Box display="flex" alignItems="center">
                      <EmailIcon
                        sx={{ color: colors.greenAccent[500], fontSize: "14px", mr: 0.5 }}
                      />
                      <Typography variant="caption" color={colors.grey[400]}>
                        {product.company_email}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Status Badges */}
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={0.5}>
                  <Chip
                    label={product.isactive ? "Active" : "Inactive"}
                    size="small"
                    color={product.isactive ? "success" : "default"}
                    sx={{ fontSize: "0.7rem" }}
                  />
                  {product.featured && (
                    <Chip
                      label="Featured"
                      size="small"
                      sx={{
                        backgroundColor: colors.greenAccent[600],
                        color: colors.grey[100],
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                  {product.sku && (
                    <Chip
                      label={`SKU: ${product.sku}`}
                      size="small"
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </Stack>

                {/* Price */}
                {product.total_amount && (
                  <Typography
                    variant="h6"
                    color={colors.greenAccent[500]}
                    fontWeight="bold"
                    mt={1}
                  >
                    £{parseFloat(product.total_amount).toFixed(2)}
                  </Typography>
                )}

                {/* Edit and Delete Buttons */}
                <Box display="flex" gap={1} mt={2} onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    fullWidth
                    onClick={() => {
                      // Placeholder for edit functionality
                      console.log("Edit product:", product._id);
                    }}
                    sx={{
                      backgroundColor: colors.blueAccent[700],
                      "&:hover": {
                        backgroundColor: colors.blueAccent[600],
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    fullWidth
                    onClick={() => {
                      // Placeholder for delete functionality
                      console.log("Delete product:", product._id);
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {products.length === 0 && !loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
          flexDirection="column"
        >
          <Typography variant="h5" color={colors.grey[300]} mb={2}>
            No products found
          </Typography>
          <Typography variant="body2" color={colors.grey[400]}>
            You don't have any products yet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PartnerProducts;

